import {
  ConversationControllerService,
  ConversationResponse
} from "@9amhealth/openapi";
import type { ToolCall } from "@9amhealth/shared";
import {
  parseNdjsonLine,
  applyNdjsonEvent,
  stripEscalationJson,
  parseStructuredContent
} from "@9amhealth/shared";
import { Cubit } from "blac-next";
import envVariables from "src/lib/envVariables";
import reportErrorSentry from "src/lib/reportErrorSentry";
import { authenticationState } from "src/state/state";

export type { ToolCall } from "@9amhealth/shared";

export interface LeapChatMessage {
  id: string;
  role: "user" | "assistant";
  text: string;
  toolCalls: ToolCall[];
  createdAt: Date;
}

export interface LeapChatState {
  conversations: ConversationResponse[];
  currentMessages: LeapChatMessage[];
  currentToolCalls: ToolCall[];
  activeConversationId: string | null;
  isRunning: boolean;
  stickyBottom: boolean;
  isInitializing: boolean;
  inputValue: string;
}

export const formatConversationDate = (dateStr: string): string => {
  const date = new Date(dateStr);
  const time = date.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit"
  });
  const diffDays = Math.floor(
    (Date.now() - date.getTime()) / (1000 * 60 * 60 * 24)
  );

  if (diffDays === 0) {
    return `Today, ${time}`;
  }
  if (diffDays === 1) {
    return `Yesterday, ${time}`;
  }
  if (diffDays < 7) {
    return `${diffDays} days ago, ${time}`;
  }
  return `${date.toLocaleDateString()}, ${time}`;
};

const normalizeMessageText = (text: string): string =>
  text
    .replace(/[^\S\n]+$/gm, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

const SCROLL_STICK_THRESHOLD = 10;

export default class LeapChatCubit extends Cubit<LeapChatState> {
  static keepAlive = true;
  private abortController: AbortController | null = null;
  autoScrollInProgress = false;
  domElements: {
    scrollEl: HTMLDivElement | null;
    contentEl: HTMLDivElement | null;
  } = {
    scrollEl: null,
    contentEl: null
  };

  constructor() {
    super({
      conversations: [],
      currentMessages: [],
      currentToolCalls: [],
      activeConversationId: null,
      isRunning: false,
      isInitializing: false,
      inputValue: "",
      stickyBottom: true
    });
  }

  private handleManualScroll = (): void => {
    if (this.state.stickyBottom) {
      this.patch({ stickyBottom: false });
    }
  };

  private handleScrollEvent = (): void => {
    const el = this.domElements.scrollEl;
    if (!el || this.autoScrollInProgress) {
      return;
    }

    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    const shouldStick = distanceFromBottom < SCROLL_STICK_THRESHOLD;

    if (shouldStick !== this.state.stickyBottom) {
      this.patch({ stickyBottom: shouldStick });
    }
  };

  setDomElements = (elements: {
    scrollEl: HTMLDivElement | null;
    contentEl: HTMLDivElement | null;
  }): void => {
    const { scrollEl } = elements;
    if (scrollEl && scrollEl.getAttribute("data-leap-scroll-el") !== "true") {
      scrollEl.addEventListener("wheel", this.handleManualScroll);
      scrollEl.addEventListener("touchstart", this.handleManualScroll);
      scrollEl.addEventListener("scroll", this.handleScrollEvent);
      scrollEl.setAttribute("data-leap-scroll-el", "true");
    }

    this.domElements = {
      scrollEl: elements.scrollEl,
      contentEl: elements.contentEl
    };
  };

  scrollToBottom = (): void => {
    const el = this.domElements.scrollEl;
    if (!el) {
      return;
    }
    this.autoScrollInProgress = true;
    el.scrollTo({ top: el.scrollHeight, behavior: "instant" });
    requestAnimationFrame(() => {
      this.autoScrollInProgress = false;
    });
  };

  restoreScrollIfNeeded = (): void => {
    if (this.state.stickyBottom) {
      this.scrollToBottom();
    }
  };

  setInputValue = (value: string): void => {
    this.patch({ inputValue: value });
  };

  submitInput = (): void => {
    const text = this.state.inputValue.trim();
    if (!text || this.state.isRunning) {
      return;
    }
    this.patch({ inputValue: "", stickyBottom: true });
    this.scrollToBottom();
    void this.sendMessage(text);
  };

  listConversations = async (): Promise<ConversationResponse[]> => {
    try {
      const res = await ConversationControllerService.listConversations(0, 50);
      const conversations = [...res.data.content].sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
      this.patch({ conversations });
      return conversations;
    } catch (e) {
      reportErrorSentry(e);
      return [];
    }
  };

  loadConversation = async (conversationId: string): Promise<void> => {
    this.patch({
      isInitializing: true,
      activeConversationId: conversationId,
      currentMessages: []
    });

    try {
      const messages = await this.fetchMessages(conversationId);
      this.patch({
        currentMessages: messages,
        isInitializing: false
      });
    } catch (e) {
      reportErrorSentry(e);
      this.patch({ isInitializing: false });
    }
  };

  startNewConversation = async (text: string): Promise<string | null> => {
    const normalized = normalizeMessageText(text);
    if (!normalized || this.state.isRunning) {
      return null;
    }

    this.patch({ isRunning: true });

    const conversationId = await this.createNewConversation();
    if (!conversationId) {
      this.patch({ isRunning: false });
      return null;
    }

    const assistantMsgId = crypto.randomUUID();

    this.patch({
      activeConversationId: conversationId,
      currentMessages: [
        {
          id: crypto.randomUUID(),
          role: "user",
          text: normalized,
          toolCalls: [],
          createdAt: new Date()
        },
        {
          id: assistantMsgId,
          role: "assistant",
          text: "",
          toolCalls: [],
          createdAt: new Date()
        }
      ],
      stickyBottom: true
    });

    this.streamResponseInBackground(conversationId, assistantMsgId, normalized);

    return conversationId;
  };

  private streamResponseInBackground(
    conversationId: string,
    assistantMsgId: string,
    text: string
  ): void {
    void this.streamResponse(conversationId, assistantMsgId, text)
      .catch((e) => {
        if (!(e instanceof DOMException && e.name === "AbortError")) {
          reportErrorSentry(e);
          this.patchAssistantMessage(
            assistantMsgId,
            "We couldn't send your message. Try again, or message your care team directly if this keeps happening."
          );
        }
      })
      .finally(() => {
        this.abortController = null;
        this.patch({ isRunning: false, stickyBottom: false });
      });
  }

  sendMessage = async (text: string): Promise<void> => {
    const normalized = normalizeMessageText(text);
    if (!normalized || this.state.isRunning) {
      return;
    }

    const { activeConversationId } = this.state;
    if (!activeConversationId) {
      return;
    }

    const assistantMsgId = crypto.randomUUID();

    this.patch({
      isRunning: true,
      currentMessages: [
        ...this.state.currentMessages,
        {
          id: crypto.randomUUID(),
          role: "user",
          text: normalized,
          toolCalls: [],
          createdAt: new Date()
        },
        {
          id: assistantMsgId,
          role: "assistant",
          text: "",
          toolCalls: [],
          createdAt: new Date()
        }
      ]
    });

    try {
      await this.streamResponse(
        activeConversationId,
        assistantMsgId,
        normalized
      );
    } catch (e) {
      if (!(e instanceof DOMException && e.name === "AbortError")) {
        reportErrorSentry(e);
        this.patchAssistantMessage(
          assistantMsgId,
          "We couldn't send your message. Try again, or message your care team directly if this keeps happening."
        );
      }
    } finally {
      this.abortController = null;
      this.patch({ isRunning: false, stickyBottom: false });
    }
  };

  clearActiveConversation = (): void => {
    this.cancelStream();
    this.patch({
      activeConversationId: null,
      currentMessages: [],
      isInitializing: false,
      isRunning: false,
      stickyBottom: true
    });
  };

  fetchMessages = async (
    conversationId: string
  ): Promise<LeapChatMessage[]> => {
    const res = await ConversationControllerService.listConversationMessages(
      conversationId,
      0,
      1000,
      "addedAt,asc"
    );
    return res.data.content
      .map((m): LeapChatMessage => {
        const isUser = m.message.senderId === m.message.messageOwnerId;
        if (isUser) {
          return {
            id: m.message.id,
            role: "user",
            text: stripEscalationJson(m.message.contentValue ?? ""),
            toolCalls: [],
            createdAt: new Date(m.addedAt)
          };
        }
        const { text, toolCalls } = parseStructuredContent(
          m.message.contentType,
          m.message.contentValue
        );
        return {
          id: m.message.id,
          role: "assistant",
          text,
          toolCalls,
          createdAt: new Date(m.addedAt)
        };
      })
      .sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());
  };

  private async createNewConversation(): Promise<string | null> {
    try {
      const res = await ConversationControllerService.createConversation({});
      return res.data.conversationId;
    } catch (e) {
      reportErrorSentry(e);
      return null;
    }
  }

  private async streamResponse(
    conversationId: string,
    assistantMsgId: string,
    text: string
  ): Promise<void> {
    this.abortController = new AbortController();

    const response = await fetch(`${envVariables.API_BASE_URL}/v1/ai/ama`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "text/event-stream",
        Authorization: `Bearer ${authenticationState.accessToken}`
      },
      body: JSON.stringify({
        contentValue: text,
        contentType: "text/plain",
        conversationId
      }),
      signal: this.abortController.signal
    });

    if (!response.ok) {
      throw new Error(`AMA request failed: ${response.status}`);
    }
    if (!response.body) {
      throw new Error("No response body");
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let fullText = "";
    let buffer = "";
    let previousWasData = false;
    const toolCalls: ToolCall[] = [];

    const processDataLine = (data: string): void => {
      const event = parseNdjsonLine(data);
      if (event) {
        fullText = applyNdjsonEvent(event, fullText, toolCalls);
        if (
          event.type === "tool_call_start" ||
          event.type === "tool_call_end"
        ) {
          this.patchAssistantMessage(assistantMsgId, fullText, [...toolCalls]);
          this.patch({ currentToolCalls: [...toolCalls] });
        } else {
          this.patchAssistantMessage(assistantMsgId, fullText);
        }
        previousWasData = false;
        return;
      }

      if (previousWasData) {
        fullText += "\n";
      }
      fullText += data;
      previousWasData = true;
      this.patchAssistantMessage(assistantMsgId, fullText);
    };

    // eslint-disable-next-line no-constant-condition, @typescript-eslint/no-unnecessary-condition
    while (true) {
      const { value, done } = await reader.read();
      if (done) {
        break;
      }

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop() ?? "";

      for (const line of lines) {
        if (line.startsWith("data:")) {
          processDataLine(line.slice(5));
        } else if (line === "") {
          previousWasData = false;
        }
      }
    }

    if (buffer.startsWith("data:")) {
      processDataLine(buffer.slice(5));
    }
  }

  private patchAssistantMessage(
    messageId: string,
    text: string,
    toolCalls?: ToolCall[]
  ): void {
    this.patch({
      currentMessages: this.state.currentMessages.map((msg) =>
        msg.id === messageId
          ? { ...msg, text, ...(toolCalls ? { toolCalls } : {}) }
          : msg
      )
    });
  }

  cancelStream = (): void => {
    this.abortController?.abort();
  };
}
